/**
 * THE §6 SETTINGS STORE — one value, one applier, one subscription.
 *
 * ┌── read ────────────────────────────────────────────────────────────────┐
 * │ localStorage (device)  ─┐                                              │
 * │ account preferences    ─┴─> normaliseA11ySettings ─> a11yStore ─> DOM  │
 * └────────────────────────────────────────────────────────────────────────┘
 * Every writer goes through `setA11ySettings`, which normalises, stores,
 * applies to the document, pushes to the account, and notifies subscribers —
 * in that order, so a subscriber that reads the DOM sees the new state.
 *
 * ── PERSISTENCE: DEVICE FIRST, ACCOUNT WHEN THE SERVER CAN HOLD IT ─────────
 *
 * The device copy is the one that is always written, and it is written
 * SYNCHRONOUSLY, because these settings must survive a reload of a player who
 * is not signed in at all — a visitor reading the landing page with a text scale
 * set has not authenticated and never will until they play.
 *
 * *** THE ACCOUNT COPY IS A CONTRACT THIS PHASE COULD NOT HONOUR. ***
 * The task asks for "persist per player through core's GET|PATCH
 * /me/preferences". That endpoint is `backend/src/core/i18n/preferences.
 * controller.ts`, whose `UpdatePreferencesDto` declares exactly two fields,
 * `locale` and `currency`, and `main.ts` installs a global `ValidationPipe`
 * with `forbidNonWhitelisted: true`. A PATCH carrying an `accessibility` key is
 * therefore a 400, not a stored setting — and the controller and its DTO are
 * both under `backend/src/core/**`, which HARD LAW 1 forbids editing.
 *
 * So the account half is built, wired and tested, and it TURNS ITSELF ON when
 * the server grows the field:
 *   • at boot, `adoptAccountA11y()` GETs `/me/preferences` (a real endpoint
 *     that answers 200 today);
 *   • if the body has no `accessibility` key, the remote is marked unsupported
 *     for the session and NO PATCH IS EVER SENT — zero 400s, zero wasted
 *     requests, zero behaviour change;
 *   • if it does, the value is adopted and every later change is PATCHed.
 * The moment a backend lane adds `accessibility` to the preferences row, this
 * starts syncing with no client change. See the phase report.
 *
 * ── WHY THE DEVICE COPY IS FOUR KEYS, NOT ONE JSON BLOB ─────────────────────
 * Two of the four keys (`rga.hud.mirrored`, `rga.hud.teamPalette`) are the ones
 * `engine/meta/menu.ts` has read and written since Phase 2, and
 * `engine/bootstrap.ts` still calls `readMenuSettings()` to build the match's
 * options. Sharing the keys means the training menu's toggle and this store are
 * the SAME setting rather than two that must be kept in step. A JSON blob would
 * have needed a migration and a second writer for the same player intent.
 */
import { api } from '@/core/network/api';
import { authStore } from '@/core/state/auth-store';
import { createStore, type Store } from '@/core/state/store';

import { applyA11ySettings } from './apply';
import { systemPrefersReducedMotion, watchSystemMotion } from './motion';
import {
  A11Y_STORAGE_KEYS,
  DEFAULT_A11Y_SETTINGS,
  a11ySettingsEqual,
  clampTextScale,
  normaliseA11ySettings,
  resolveReducedMotion,
  type A11ySettings,
} from './settings';

/* ═════════════════════════════════════════════════════════════ storage ══ */

/** `localStorage`, or null in private mode / SSR / a locked-down iframe. */
function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    if (s === undefined || s === null) return null;
    // Safari private mode has the object and throws on write. Probe once.
    const probe = '__rga_a11y__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

function readDevice(): A11ySettings {
  const s = storage();
  if (s === null) return DEFAULT_A11Y_SETTINGS;
  try {
    return normaliseA11ySettings({
      mirrored: s.getItem(A11Y_STORAGE_KEYS.mirrored) === '1',
      teamPalette: s.getItem(A11Y_STORAGE_KEYS.teamPalette),
      motion: s.getItem(A11Y_STORAGE_KEYS.motion),
      textScale: s.getItem(A11Y_STORAGE_KEYS.textScale),
      // Absent -> undefined -> the default (on). `'0'` is the only off value.
      announcements: s.getItem(A11Y_STORAGE_KEYS.announcements) ?? undefined,
    });
  } catch {
    return DEFAULT_A11Y_SETTINGS;
  }
}

function writeDevice(next: A11ySettings): void {
  const s = storage();
  if (s === null) return;
  try {
    s.setItem(A11Y_STORAGE_KEYS.mirrored, next.mirrored ? '1' : '0');
    s.setItem(A11Y_STORAGE_KEYS.teamPalette, next.teamPalette);
    s.setItem(A11Y_STORAGE_KEYS.motion, next.motion);
    s.setItem(A11Y_STORAGE_KEYS.textScale, String(clampTextScale(next.textScale)));
    s.setItem(A11Y_STORAGE_KEYS.announcements, next.announcements ? '1' : '0');
  } catch {
    /* quota or denied: the setting is in force, it is simply not remembered */
  }
}

/* ══════════════════════════════════════════════════════════════ the port ══ */

/**
 * The seam every test injects and the app never does. Same shape as
 * `engine/layout/currency.ts`'s `Deps`, deliberately: two modules that both own
 * one half of `/me/preferences` should be read the same way.
 */
export interface A11yDeps {
  /** Reads the account's stored record. `null` = the server has no field. */
  loadAccount(): Promise<unknown>;
  /** Writes it. Never called while `remoteSupported` is false. */
  saveAccount(settings: A11ySettings): Promise<void>;
  isAuthenticated(): boolean;
  document(): Document | null;
  systemPrefersReduced(): boolean;
}

/** The wire shape, if and when the server carries one. */
interface PreferencesBody {
  readonly accessibility?: unknown;
}

const defaultDeps: A11yDeps = {
  loadAccount: async () => {
    const body = await api.get<PreferencesBody>('/me/preferences');
    // `undefined` and only `undefined` means "this server does not know the
    // field". `null` means "it knows it and the player has not chosen", which
    // is a supported remote with nothing stored.
    return body === null || body === undefined ? undefined : body.accessibility;
  },
  saveAccount: async (settings) => {
    await api.patch('/me/preferences', { accessibility: settings });
  },
  isAuthenticated: () => authStore.get().user !== null,
  document: () => (typeof document === 'undefined' ? null : document),
  systemPrefersReduced: systemPrefersReducedMotion,
};

let deps: A11yDeps = defaultDeps;

/** Test seam. Pass a partial; the rest stays real. */
export function setA11yDeps(next: Partial<A11yDeps>): void {
  deps = { ...defaultDeps, ...next };
}

/* ═══════════════════════════════════════════════════════════════ state ══ */

export interface A11yState {
  readonly settings: A11ySettings;
  /** The RESOLVED answer the match and the DOM both use. */
  readonly reducedMotion: boolean;
  /** `false` once the server has proved it cannot hold these. */
  readonly remoteSupported: boolean;
  /**
   * Has the server been ASKED yet?
   *
   * The push is gated on PROVEN support, not on "not yet disproven". Without
   * it, every setting a player changed between the first paint and the boot
   * GET landing would be a PATCH carrying a field core rejects — a 400 per
   * change, swallowed, achieving nothing. Optimism costs requests here and
   * silence costs none.
   */
  readonly remoteProbed: boolean;
  /** Has `initA11y()` run? Screens use it to avoid painting a guess. */
  readonly ready: boolean;
}

function stateFor(
  settings: A11ySettings,
  remoteSupported: boolean,
  remoteProbed: boolean,
  ready: boolean,
): A11yState {
  return Object.freeze({
    settings,
    reducedMotion: resolveReducedMotion(settings, deps.systemPrefersReduced()),
    remoteSupported,
    remoteProbed,
    ready,
  });
}

const INITIAL: A11yState = Object.freeze({
  settings: DEFAULT_A11Y_SETTINGS,
  reducedMotion: false,
  remoteSupported: true,
  remoteProbed: false,
  ready: false,
});

export const a11yStore: Store<A11yState> = createStore<A11yState>(INITIAL);

/** The current settings. Cheap; safe to call in a render. */
export function a11ySettings(): A11ySettings {
  return a11yStore.get().settings;
}

/** The resolved `prefers-reduced-motion` answer, explicit setting included. */
export function a11yReducedMotion(): boolean {
  return a11yStore.get().reducedMotion;
}

/* ═════════════════════════════════════════════════════════════ mutation ══ */

let stopWatchingSystem: (() => void) | null = null;

/**
 * Apply, persist and publish. THE ONLY MUTATOR.
 *
 * `persist` is false for a value that came FROM a store (the account read, the
 * OS media query firing), so adopting a remote value cannot bounce straight
 * back at the remote as a write.
 */
function commit(next: A11ySettings, persist: boolean): void {
  const prev = a11yStore.get();
  const settings = normaliseA11ySettings(next);
  const changed = !a11ySettingsEqual(prev.settings, settings);
  if (!changed && prev.ready) {
    // Still re-resolve: the OS query may have flipped under an unchanged record.
    const resolved = resolveReducedMotion(settings, deps.systemPrefersReduced());
    if (resolved === prev.reducedMotion) return;
  }
  if (persist) writeDevice(settings);
  const doc = deps.document();
  if (doc !== null) applyA11ySettings(doc, settings, deps.systemPrefersReduced());
  a11yStore.set(stateFor(settings, prev.remoteSupported, prev.remoteProbed, true));
  if (persist && changed) void pushToAccount(settings);
}

async function pushToAccount(settings: A11ySettings): Promise<void> {
  const state = a11yStore.get();
  if (!state.remoteSupported || !state.remoteProbed) return;
  if (!deps.isAuthenticated()) return;
  try {
    await deps.saveAccount(settings);
  } catch {
    // A rejected write is not an error a player can act on: the setting is
    // already in force on this device and already stored on it. The next
    // change tries again. (A 400 from a server with no such field is caught
    // at boot by `adoptAccountA11y`, which switches the remote off entirely.)
  }
}

/** Replace the whole record. */
export function setA11ySettings(next: A11ySettings): void {
  commit(next, true);
}

/** Change one field. The form every control uses. */
export function updateA11ySettings(patch: Partial<A11ySettings>): void {
  commit({ ...a11yStore.get().settings, ...patch }, true);
}

/* ════════════════════════════════════════════════════════════════ boot ══ */

/**
 * Read the device copy, apply it, and start following the OS media query.
 *
 * SYNCHRONOUS AND CALLED BEFORE THE FIRST RENDER. A text scale applied after
 * the first paint is a reflow the player watches happen, and a reduced-motion
 * player would see one animation before the kill-switch lands — which is the
 * one animation they asked not to see.
 *
 * Idempotent: calling it twice re-applies and re-subscribes once.
 */
export function initA11y(): A11ySettings {
  stopWatchingSystem?.();
  const settings = readDevice();
  // `persist: false` — this value CAME from storage; writing it back would be
  // a no-op with a chance of a quota error on every boot.
  commit(settings, false);
  stopWatchingSystem = watchSystemMotion(() => {
    // The record is unchanged; only its resolution moved. Re-commit so the DOM
    // attribute, the store and every subscriber (the live match included)
    // follow the OS without a reload.
    commit(a11yStore.get().settings, false);
  });
  return a11yStore.get().settings;
}

/** Stop following the OS query. Tests and teardown. */
export function disposeA11y(): void {
  stopWatchingSystem?.();
  stopWatchingSystem = null;
}

/**
 * Adopt the account's copy, if this server has one. Fire-and-forget at boot and
 * after a sign-in; never awaited on a render path.
 *
 * ORDER MATTERS AND IT IS "SERVER WINS, ONCE". The account value is the one a
 * player set deliberately, possibly on another device; the device value is
 * whatever this browser last saw. On a fresh device the account value is the
 * only thing that can restore a setting, so it is adopted. It is adopted with
 * `persist: false` and then written to the device copy explicitly, so the two
 * agree afterwards without the adoption looking like a change to push back.
 */
export async function adoptAccountA11y(): Promise<void> {
  if (!deps.isAuthenticated()) return;
  if (!a11yStore.get().remoteSupported) return;
  let raw: unknown;
  try {
    raw = await deps.loadAccount();
  } catch {
    // A dead network is not proof the field is missing — leave the remote on
    // and try again at the next sign-in.
    return;
  }
  if (raw === undefined) {
    // The server answered and had no such field. Stop writing to it, for good,
    // for this session. This is the branch that runs against core today.
    const s = a11yStore.get();
    a11yStore.set(Object.freeze({ ...s, remoteSupported: false, remoteProbed: true }));
    return;
  }
  {
    const s = a11yStore.get();
    a11yStore.set(Object.freeze({ ...s, remoteProbed: true }));
  }
  if (raw === null) return; // Supported, nothing stored yet. Device wins.
  const settings = normaliseA11ySettings(raw);
  if (a11ySettingsEqual(settings, a11yStore.get().settings)) return;
  commit(settings, false);
  writeDevice(settings);
}

/** Test seam: forget everything, including the OS subscription. */
export function resetA11yForTests(): void {
  disposeA11y();
  deps = defaultDeps;
  a11yStore.set(INITIAL);
}
